// Prestashop-style multi-category picker for the product editor.
//
// Renders the ACTIVE category tree with checkboxes (a product can live in any
// number of categories) plus a per-row radio choosing the MAIN category — the
// one persisted at product_categories.position 0, which the storefront uses
// for breadcrumbs/canonical placement. Includes search (matches + their
// ancestors), expand/collapse, and removable chips of the current selection.
//
// Inactive legacy categories are hidden from the tree; if the product is
// already assigned to one, it surfaces as a dimmed chip so the assignment is
// visible and removable rather than silently dropped on save.

import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/i18n";

export type PickerCategory = {
  id: string;
  parent_id: string | null;
  slug: string;
  active: boolean;
  translations: any;
};

const nameOf = (c: PickerCategory) =>
  c.translations?.lv?.name ?? c.translations?.en?.name ?? c.slug;

export function CategoryTreePicker({
  categories,
  selected,
  mainId,
  onChange,
}: {
  categories: PickerCategory[];
  selected: Set<string>;
  mainId: string | null;
  onChange: (selected: Set<string>, mainId: string | null) => void;
}) {
  const t = useT();
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    // Start with ancestors of the current selection open so edits land in view.
    const byId = new Map(categories.map((c) => [c.id, c]));
    const open = new Set<string>();
    for (const id of selected) {
      let cur = byId.get(id);
      while (cur?.parent_id) {
        open.add(cur.parent_id);
        cur = byId.get(cur.parent_id);
      }
    }
    return open;
  });

  const byId = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const childrenOf = useMemo(() => {
    const m = new Map<string | null, PickerCategory[]>();
    for (const c of categories.filter((c) => c.active)) {
      const list = m.get(c.parent_id) ?? [];
      list.push(c);
      m.set(c.parent_id, list);
    }
    return m;
  }, [categories]);

  // Search: keep rows whose name matches + all their ancestors (auto-open).
  const q = query.trim().toLowerCase();
  const visibleIds = useMemo(() => {
    if (!q) return null; // null = no filter
    const keep = new Set<string>();
    for (const c of categories) {
      if (!c.active) continue;
      if (String(nameOf(c)).toLowerCase().includes(q)) {
        keep.add(c.id);
        let cur = c;
        while (cur.parent_id) {
          keep.add(cur.parent_id);
          cur = byId.get(cur.parent_id)!;
          if (!cur) break;
        }
      }
    }
    return keep;
  }, [q, categories, byId]);

  const toggle = (id: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    let nextMain = mainId;
    if (checked && (!nextMain || !next.has(nextMain))) nextMain = id;
    if (!checked && nextMain === id) nextMain = next.values().next().value ?? null;
    onChange(next, nextMain);
  };

  const setAllExpanded = (open: boolean) => {
    if (!open) return setExpanded(new Set());
    setExpanded(new Set(categories.filter((c) => (childrenOf.get(c.id) ?? []).length).map((c) => c.id)));
  };

  const renderRows = (parentId: string | null, depth: number): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];
    for (const c of childrenOf.get(parentId) ?? []) {
      if (visibleIds && !visibleIds.has(c.id)) continue;
      const kids = childrenOf.get(c.id) ?? [];
      const isOpen = !!q || expanded.has(c.id);
      const isChecked = selected.has(c.id);
      rows.push(
        <div
          key={c.id}
          className="flex items-center gap-2 py-1"
          style={{ paddingLeft: depth * 20 }}
        >
          {kids.length > 0 ? (
            <button
              type="button"
              onClick={() =>
                setExpanded((prev) => {
                  const n = new Set(prev);
                  n.has(c.id) ? n.delete(c.id) : n.add(c.id);
                  return n;
                })
              }
              className="text-muted-foreground hover:text-foreground"
              aria-label={isOpen ? "Collapse" : "Expand"}
            >
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <span className="inline-block w-[14px]" />
          )}
          <Checkbox
            id={`cat-${c.id}`}
            checked={isChecked}
            onCheckedChange={(v) => toggle(c.id, v === true)}
          />
          <label htmlFor={`cat-${c.id}`} className="flex-1 cursor-pointer font-body text-sm">
            {nameOf(c)}
          </label>
          <input
            type="radio"
            name="main-category"
            checked={mainId === c.id}
            disabled={!isChecked}
            onChange={() => onChange(new Set(selected), c.id)}
            title={t("productEditor.mainCategory", "Galvenā kategorija")}
            className="h-3.5 w-3.5 accent-primary disabled:opacity-30"
          />
        </div>,
      );
      if (kids.length > 0 && isOpen) rows.push(...renderRows(c.id, depth + 1));
    }
    return rows;
  };

  const chips = [...selected]
    .map((id) => byId.get(id))
    .filter(Boolean) as PickerCategory[];

  return (
    <div className="rounded-md border border-border bg-background p-3 space-y-3">
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("productEditor.searchCategories", "Meklēt kategorijas…")}
        className="h-8"
      />

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <span
              key={c.id}
              className={`inline-flex items-center gap-1 rounded px-2 py-0.5 font-body text-xs ${
                c.active
                  ? "bg-primary/10 text-primary"
                  : "bg-muted text-muted-foreground line-through"
              } ${mainId === c.id ? "ring-1 ring-primary" : ""}`}
              title={c.active ? undefined : t("productEditor.inactiveCategory", "Neaktīva kategorija")}
            >
              {nameOf(c)}
              <button type="button" onClick={() => toggle(c.id, false)} aria-label="Remove">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between font-body text-[11px] uppercase tracking-wide text-muted-foreground">
        <span className="flex gap-3">
          <button type="button" className="hover:text-foreground" onClick={() => setAllExpanded(true)}>
            {t("productEditor.expandAll", "Izvērst")}
          </button>
          <button type="button" className="hover:text-foreground" onClick={() => setAllExpanded(false)}>
            {t("productEditor.collapseAll", "Sakļaut")}
          </button>
        </span>
        <span>{t("productEditor.mainCategory", "Galvenā kategorija")} →</span>
      </div>

      <div className="max-h-72 overflow-y-auto pr-1">{renderRows(null, 0)}</div>
    </div>
  );
}
